import { useEffect, useMemo, useState } from 'react';
import { Plus, X, Clock } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Skeleton,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useCacheConfig,
  useUpdateCacheConfig,
  useDeleteCacheConfig,
  type CacheConfig,
} from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { configTone, configLabel } from '../shared';
import { CdnAspectDrift } from '../_shared/VendorDrift';
import { CachePolicyRules } from './CachePolicyRules';

// ── Form model ────────────────────────────────────────────────────────────
// Mirrors the legacy CacheFormValues; comma-joined strings for list inputs.
interface CacheForm {
  enabled: boolean;
  bypassCache: boolean;
  ignoreClientNoCache: boolean;
  defaultTTL: string;
  maxTTL: string;
  edgeTTL: string;
  browserTTL: string;
  staleWhileRevalidate: string;
  staleIfError: string;
  customCacheKey: boolean;
  queryStringStrategy: string;
  queryParams: string;
  sortQueryParams: boolean;
  cachingHeaders: string;
  cachingCookies: string;
  detectDeviceType: boolean;
  detectGeoIP: boolean;
  detectLanguage: boolean;
  customVaryHandling: boolean;
  normalizeAcceptEncoding: boolean;
  varyAllowlist: string;
  statusTTL: { status: string; edgeTtl: string; browserTtl: string }[];
}

// Common status codes offered as quick-add chips for Per-Status TTL.
const STATUS_QUICK_ADD = ['400', '401', '403', '404', '429', '500', '502', '503', '504'];

const DEFAULTS: CacheForm = {
  enabled: true,
  bypassCache: false,
  ignoreClientNoCache: true,
  defaultTTL: '3600',
  maxTTL: '2592000',
  edgeTTL: '0',
  browserTTL: '0',
  staleWhileRevalidate: '0',
  staleIfError: '0',
  customCacheKey: false,
  queryStringStrategy: 'all',
  queryParams: '',
  sortQueryParams: false,
  cachingHeaders: '',
  cachingCookies: '',
  detectDeviceType: false,
  detectGeoIP: false,
  detectLanguage: false,
  customVaryHandling: false,
  normalizeAcceptEncoding: true,
  varyAllowlist: '',
  statusTTL: [],
};

const csv = (arr: unknown): string => (Array.isArray(arr) ? arr.join(', ') : '');
const splitCsv = (s: string): string[] => s.split(',').map((x) => x.trim()).filter(Boolean);

function hydrate(cfg: CacheConfig): CacheForm {
  const key = cfg.cache_key ?? null;
  const vary = cfg.vary ?? null;
  return {
    enabled: cfg.enabled ?? true,
    bypassCache: cfg.bypass_cache ?? false,
    ignoreClientNoCache: cfg.ignore_client_no_cache ?? true,
    defaultTTL: String(cfg.default_ttl_secs ?? 3600),
    maxTTL: String(cfg.max_ttl_secs ?? 2592000),
    edgeTTL: String(cfg.edge_ttl_secs ?? 0),
    browserTTL: String(cfg.browser_ttl_secs ?? 0),
    staleWhileRevalidate: String(cfg.stale_while_revalidate_secs ?? 0),
    staleIfError: String(cfg.stale_if_error_secs ?? 0),
    customCacheKey: !!key,
    queryStringStrategy: key?.query_strategy ?? 'all',
    queryParams: csv(key?.query_params),
    sortQueryParams: key?.query_sort ?? false,
    cachingHeaders: csv(key?.header_include),
    cachingCookies: csv(key?.cookie_include),
    detectDeviceType: key?.device_type ?? false,
    detectGeoIP: key?.geo ?? false,
    detectLanguage: key?.lang ?? false,
    customVaryHandling: !!vary,
    normalizeAcceptEncoding: vary?.normalize_accept_encoding ?? true,
    varyAllowlist: csv(vary?.allowlist),
    statusTTL: (() => {
      const edge = cfg.status_ttl ?? {};
      const browser = cfg.status_browser_ttl ?? {};
      const codes = Array.from(new Set([...Object.keys(edge), ...Object.keys(browser)]));
      return codes.map((status) => ({
        status,
        edgeTtl: String(edge[status] ?? 0),
        browserTtl: String(browser[status] ?? 0),
      }));
    })(),
  };
}

function toPayload(v: CacheForm): CacheConfig {
  return {
    enabled: v.enabled,
    bypass_cache: v.bypassCache,
    ignore_client_no_cache: v.ignoreClientNoCache,
    default_ttl_secs: Number(v.defaultTTL) || 0,
    max_ttl_secs: Number(v.maxTTL) || 0,
    edge_ttl_secs: Number(v.edgeTTL) || 0,
    browser_ttl_secs: Number(v.browserTTL) || 0,
    stale_while_revalidate_secs: Number(v.staleWhileRevalidate) || 0,
    stale_if_error_secs: Number(v.staleIfError) || 0,
    cache_key: v.customCacheKey
      ? {
          query_strategy: v.queryStringStrategy,
          query_params: splitCsv(v.queryParams),
          query_sort: v.sortQueryParams,
          header_include: splitCsv(v.cachingHeaders),
          cookie_include: splitCsv(v.cachingCookies),
          device_type: v.detectDeviceType,
          geo: v.detectGeoIP,
          lang: v.detectLanguage,
        }
      : null,
    vary: v.customVaryHandling
      ? {
          enabled: true,
          allowlist: splitCsv(v.varyAllowlist),
          normalize_accept_encoding: v.normalizeAcceptEncoding,
        }
      : null,
    status_ttl: v.statusTTL.reduce<Record<string, number>>((acc, { status, edgeTtl }) => {
      const code = status.trim();
      if (code) acc[code] = Number(edgeTtl) || 0;
      return acc;
    }, {}),
    status_browser_ttl: v.statusTTL.reduce<Record<string, number>>((acc, { status, browserTtl }) => {
      const code = status.trim();
      if (code) acc[code] = Number(browserTtl) || 0;
      return acc;
    }, {}),
  };
}

export function CacheTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useCacheConfig(domainId);
  const isConfigured = !!cfg;

  const [form, setForm] = useState<CacheForm>(DEFAULTS);
  const [snapshot, setSnapshot] = useState<CacheForm>(DEFAULTS);

  useEffect(() => {
    const next = cfg ? hydrate(cfg) : DEFAULTS;
    setForm(next);
    setSnapshot(next);
  }, [cfg]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(snapshot), [form, snapshot]);
  const set = <K extends keyof CacheForm>(k: K, val: CacheForm[K]) =>
    setForm((f) => ({ ...f, [k]: val }));

  // ── Per-Status TTL row helpers ──
  const addStatusRow = (status = '') =>
    setForm((f) =>
      f.statusTTL.some((r) => r.status === status && status)
        ? f
        : { ...f, statusTTL: [...f.statusTTL, { status, edgeTtl: '0', browserTtl: '0' }] },
    );
  const setStatusRow = (i: number, key: 'status' | 'edgeTtl' | 'browserTtl', val: string) =>
    setForm((f) => ({
      ...f,
      statusTTL: f.statusTTL.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)),
    }));
  const removeStatusRow = (i: number) =>
    setForm((f) => ({ ...f, statusTTL: f.statusTTL.filter((_, idx) => idx !== i) }));
  const usedStatuses = new Set(form.statusTTL.map((r) => r.status));

  const update = useUpdateCacheConfig(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cache', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Cache configuration saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save cache configuration', description: errorMessage(e, 'Could not save cache configuration') }),
  });
  const remove = useDeleteCacheConfig(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cache', 'delete');
      show({ tone: 'success', title: 'Cache configuration removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove cache configuration', description: errorMessage(e, 'Could not remove cache configuration') }),
  });

  const busy = update.isPending || remove.isPending;

  if (isLoading) {
    return (
      <Section title="Cache">
        <Skeleton style={{ height: 280 }} />
      </Section>
    );
  }

  return (
    <Section
      title="Cache"
      description={`Edge cache TTLs, key strategy, and behavior for ${domain}.`}
      actions={<StatusPill tone={configTone(isConfigured)}>{configLabel(isConfigured)}</StatusPill>}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <CdnAspectDrift domainId={domainId} path="cache" />
        {/* ── Behavior ── */}
        <Card>
          <CardHeader>
            <CardTitle>Behavior</CardTitle>
            <CardDescription>How the edge decides what to cache.</CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <SettingRow
              title="Caching enabled"
              description="Serve responses from the edge cache."
              control={<Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />}
            />
            <SettingRow
              title="Bypass cache"
              description="Always fetch from the origin (no edge caching)."
              control={<Switch checked={form.bypassCache} onCheckedChange={(v) => set('bypassCache', v)} />}
            />
            <SettingRow
              title="Ignore client no-cache"
              description="Ignore Cache-Control: no-cache from the client."
              control={
                <Switch
                  checked={form.ignoreClientNoCache}
                  onCheckedChange={(v) => set('ignoreClientNoCache', v)}
                />
              }
            />
          </CardBody>
        </Card>

        {/* ── TTLs ── */}
        <Card>
          <CardHeader>
            <CardTitle>TTLs</CardTitle>
            <CardDescription>Time-to-live, in seconds, for cached responses.</CardDescription>
          </CardHeader>
          <CardBody>
            <div
              style={{
                display: 'grid',
                gap: 'var(--vn-space-4)',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              }}
            >
              {(
                [
                  ['defaultTTL', 'Default TTL'],
                  ['maxTTL', 'Max TTL'],
                  ['edgeTTL', 'Edge TTL'],
                  ['browserTTL', 'Browser TTL'],
                  ['staleWhileRevalidate', 'Stale while revalidate'],
                  ['staleIfError', 'Stale if error'],
                ] as [keyof CacheForm, string][]
              ).map(([k, label]) => (
                <Field key={k} label={label}>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      value={String(form[k])}
                      onChange={(e) => set(k, e.target.value as CacheForm[typeof k])}
                    />
                  )}
                </Field>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* ── Cache key ── */}
        <Card>
          <CardHeader>
            <CardTitle>Cache key</CardTitle>
            <CardDescription>What makes two requests resolve to the same cached object.</CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <SettingRow
              title="Custom cache key"
              description="Override the default cache key strategy."
              control={
                <Switch checked={form.customCacheKey} onCheckedChange={(v) => set('customCacheKey', v)} />
              }
            />
            {form.customCacheKey && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)', marginTop: 'var(--vn-space-3)' }}>
                <Field label="Query string strategy">
                  {(props) => (
                    <Select
                      value={form.queryStringStrategy}
                      onValueChange={(v) => set('queryStringStrategy', v)}
                    >
                      <SelectTrigger {...props}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Include all query params</SelectItem>
                        <SelectItem value="none">Ignore all query params</SelectItem>
                        <SelectItem value="include">Include listed params</SelectItem>
                        <SelectItem value="exclude">Exclude listed params</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                <Field label="Query params" description="Comma-separated, used by include/exclude strategies.">
                  {(props) => (
                    <Input
                      {...props}
                      value={form.queryParams}
                      placeholder="utm_source, ref"
                      onChange={(e) => set('queryParams', e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Headers in key" description="Comma-separated header names.">
                  {(props) => (
                    <Input
                      {...props}
                      value={form.cachingHeaders}
                      placeholder="Accept, X-Region"
                      onChange={(e) => set('cachingHeaders', e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Cookies in key" description="Comma-separated cookie names.">
                  {(props) => (
                    <Input
                      {...props}
                      value={form.cachingCookies}
                      placeholder="session, locale"
                      onChange={(e) => set('cachingCookies', e.target.value)}
                    />
                  )}
                </Field>
                <SettingRow
                  title="Sort query params"
                  description="Normalize param order so ?a=1&b=2 and ?b=2&a=1 share a cache entry."
                  control={
                    <Switch checked={form.sortQueryParams} onCheckedChange={(v) => set('sortQueryParams', v)} />
                  }
                />
                <SettingRow
                  title="Detect device type"
                  control={
                    <Switch checked={form.detectDeviceType} onCheckedChange={(v) => set('detectDeviceType', v)} />
                  }
                />
                <SettingRow
                  title="Detect GeoIP"
                  control={<Switch checked={form.detectGeoIP} onCheckedChange={(v) => set('detectGeoIP', v)} />}
                />
                <SettingRow
                  title="Detect language"
                  control={
                    <Switch checked={form.detectLanguage} onCheckedChange={(v) => set('detectLanguage', v)} />
                  }
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* ── Vary ── */}
        <Card>
          <CardHeader>
            <CardTitle>Vary</CardTitle>
            <CardDescription>Control how the Vary response header affects caching.</CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <SettingRow
              title="Custom Vary handling"
              control={
                <Switch
                  checked={form.customVaryHandling}
                  onCheckedChange={(v) => set('customVaryHandling', v)}
                />
              }
            />
            {form.customVaryHandling && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)', marginTop: 'var(--vn-space-3)' }}>
                <SettingRow
                  title="Normalize Accept-Encoding"
                  description="Collapse encodings to gzip / br / identity."
                  control={
                    <Switch
                      checked={form.normalizeAcceptEncoding}
                      onCheckedChange={(v) => set('normalizeAcceptEncoding', v)}
                    />
                  }
                />
                <Field label="Vary allowlist" description="Comma-separated Vary header values to honor.">
                  {(props) => (
                    <Input
                      {...props}
                      value={form.varyAllowlist}
                      placeholder="Accept-Encoding, Accept-Language"
                      onChange={(e) => set('varyAllowlist', e.target.value)}
                    />
                  )}
                </Field>
              </div>
            )}
          </CardBody>
        </Card>

        {/* ── Per-Status TTL ── */}
        <Card>
          <CardHeader
            actions={
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Plus size={14} />}
                onClick={() => addStatusRow()}
              >
                Add
              </Button>
            }
          >
            <CardTitle>Per-Status TTL</CardTitle>
            <CardDescription>
              Override cache TTL for specific HTTP status codes. Edge TTL controls how long the CDN
              caches. Browser TTL controls Cache-Control max-age sent to browsers (0 = no-store).
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Quick add:</span>
              {STATUS_QUICK_ADD.map((code) => (
                <Badge
                  key={code}
                  tone={usedStatuses.has(code) ? 'neutral' : 'info'}
                  appearance="subtle"
                  size="sm"
                  style={{ cursor: usedStatuses.has(code) ? 'default' : 'pointer', opacity: usedStatuses.has(code) ? 0.5 : 1 }}
                  onClick={() => !usedStatuses.has(code) && addStatusRow(code)}
                >
                  {code}
                </Badge>
              ))}
            </div>
            {form.statusTTL.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<Clock size={20} />}
                title="No per-status TTL overrides yet"
                description="Status codes fall back to the global TTLs above."
              />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                  <span style={{ width: 130, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                    Status
                  </span>
                  <span style={{ width: 12 }} />
                  <span style={{ width: 180, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                    Edge TTL (s)
                  </span>
                  <span style={{ width: 180, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                    Browser TTL (s)
                  </span>
                </div>
                {form.statusTTL.map((row, i) => (
                  <div key={i} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                    <Input
                      aria-label="Status code"
                      value={row.status}
                      placeholder="404"
                      onChange={(e) => setStatusRow(i, 'status', e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                      style={{ width: 130 }}
                    />
                    <span style={{ width: 12, textAlign: 'center', color: 'var(--vn-fg-muted)' }}>=</span>
                    <Input
                      aria-label="Edge TTL seconds"
                      type="number"
                      value={row.edgeTtl}
                      onChange={(e) => setStatusRow(i, 'edgeTtl', e.target.value)}
                      style={{ width: 180 }}
                    />
                    <Input
                      aria-label="Browser TTL seconds"
                      type="number"
                      value={row.browserTtl}
                      onChange={(e) => setStatusRow(i, 'browserTtl', e.target.value)}
                      style={{ width: 180 }}
                    />
                    <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => removeStatusRow(i)}>
                      <X size={15} />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <ActionBar
          start={
            isConfigured ? (
              <Button variant="secondary" disabled={busy} onClick={() => remove.mutate()}>
                Remove
              </Button>
            ) : undefined
          }
          end={
            <>
              {isConfigured && (
                <Button variant="secondary" disabled={!dirty || busy} onClick={() => setForm(snapshot)}>
                  Cancel
                </Button>
              )}
              <Button
                disabled={(isConfigured && !dirty) || busy}
                onClick={() => update.mutate(toPayload(form))}
              >
                {update.isPending ? 'Saving…' : isConfigured ? 'Save' : 'Create'}
              </Button>
            </>
          }
        />

        {/* ── Cache Policy Rules (separate /cache-policies resource) ── */}
        <CachePolicyRules domain={domain} domainId={domainId} />
      </div>
    </Section>
  );
}
